import { useEffect, useState } from "react"
import Loader from "src/components/common/loader/loader"
import { EngagementService } from "@services/engagements/engagementService"
import { AdGroupService } from "@services/engagements/adGroups/adGroupService"
import { EngagementAnalytics } from "@models/EngagementAnalytics.model"
import Conversions from "./components/conversions/Conversions"
import Spend from "./components/spend/Spend"
import Revenue from "./components/revenue/Revenue"
import Audience from "./components/audience/Audience"
import AdPerformance from "./components/adPerformance/AdPerformance"
import type { EngagementAdTags } from "@services/engagements/commands/cmdGetEngagementAdTags"
import Management from "./components/management/Management"
import MonitorDateFilter from "./components/MonitorDateFilter"
import EmailSmsPerformance from "./components/emailSms/EmailSmsPerformance"
import { useSnapshot } from "valtio"
import { appStore } from "src/store/appStore"
import {
  SequenceService,
  type MonitorSummary,
} from "@services/sequence/sequenceService"
import PerformanceSnapshot from "./components/PerformanceSnapshot"
import {
  DEFAULT_DATE_FILTER,
  type DateFilterKey,
  type DateRange,
  formatFlightDate,
  formatStartDate,
  parseApiDate,
  resolveDateFilter,
  toApiDate,
} from "./dateFilter"

/*
    One scope's charts: an objective, a campaign, or one audience within a
    campaign. Same sections either way — only the filter sent to the analytics
    endpoint differs.

    The sections are tabs rather than one long column. Six sections stacked put
    Management five screens below the numbers it explains, and every one of them
    rendered on arrival whether or not it was ever scrolled to.

    No audience picker here: audiences are rows in the table, so a campaign
    always renders all of its audiences and an audience-scoped view is arrived at
    already filtered. The head names which one rather than offering a switch.
*/

/*
    Management is not one of these: it stays beside the charts rather than behind
    a tab, because it is the standing context for whatever is being read — key
    dates, todos, the last file load — not another way to read the numbers.
*/
const SECTIONS = [
  { key: "conversions", label: "Conversions" },
  { key: "spend", label: "Spend" },
  { key: "revenue", label: "Revenue" },
  { key: "audience", label: "Audience" },
  { key: "ads", label: "Ads" },
  /*
      After Ads, as the ticket asks (MPN-2208). Only for users who hold the Email & SMS
      feature: without it the endpoint 403s, and a tab that can only ever show an error is
      worse than no tab.
  */
  { key: "emailSms", label: "Email & SMS", feature: "Email & SMS" },
] as const

type SectionKey = (typeof SECTIONS)[number]["key"]

type Props = {
  engagementId: number
  title: string
  scopeLabel: string
  launchId?: number
  objectiveId?: number
  audienceId?: number
  onBack: () => void
}

export default function MonitorDashboard({
  engagementId,
  title,
  scopeLabel,
  launchId,
  objectiveId,
  audienceId,
  onBack,
}: Props) {
  const [chartData, setChartData] = useState<EngagementAnalytics>(new EngagementAnalytics())
  const [isLoading, setIsLoading] = useState(true)
  const [audienceName, setAudienceName] = useState<string | null>(null)
  const [section, setSection] = useState<SectionKey>("conversions")
  const [adTags, setAdTags] = useState<EngagementAdTags>({})

  /*
      The Date Filter (MPN-1530 §1.2). One range for the whole page: it scopes the
      single analytics request, so the Performance Snapshot and every tab below it
      describe the same span of days rather than each chart choosing its own.
  */
  const [filterKey, setFilterKey] = useState<DateFilterKey>(DEFAULT_DATE_FILTER)
  const [range, setRange] = useState<DateRange>(() => resolveDateFilter(DEFAULT_DATE_FILTER))

  const start = toApiDate(range.start)
  const end = toApiDate(range.end)

  /*
      Email & SMS results (MPN-2208). Its own request rather than part of the analytics
      payload: the numbers come from `delivery.Message`, not from the ad networks, and
      folding them into the analytics response would make every other tab wait on them.
  */
  const { loggedInUser } = useSnapshot(appStore)
  const canSeeEmailSms = loggedInUser?.permissions?.hasAccess?.("Email & SMS") ?? false
  const [emailSms, setEmailSms] = useState<MonitorSummary | null>(null)
  const [emailSmsOn, setEmailSmsOn] = useState(false)
  const [emailSmsFailed, setEmailSmsFailed] = useState(false)

  useEffect(() => {
    if (!canSeeEmailSms || !launchId) return
    let cancelled = false
    setEmailSmsFailed(false)
    new SequenceService()
      .getLaunchMonitor(launchId, start, end)
      .then((response) => {
        if (cancelled) return
        setEmailSms(response)
        // A campaign with nothing sent and no playbook bound has the channel off; the tab
        // says so rather than drawing a page of zeroes.
        setEmailSmsOn(
          response.email.sent > 0 ||
            response.sms.sent > 0 ||
            response.email_rows.length > 0 ||
            response.sms_rows.length > 0,
        )
      })
      .catch((error) => {
        /*
            Said out loud rather than dressed as an empty state. Every error used to leave
            `emailSmsOn` false, so a 500 or a dropped connection told the reader "email and
            text messages aren't on for this campaign" -- a sentence about configuration,
            for a fault in the request. The 403 case cannot reach here anyway: the tab is
            hidden for users without the feature.
        */
        if (cancelled) return
        console.error("Monitor email & SMS:", error)
        setEmailSms(null)
        setEmailSmsFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [canSeeEmailSms, launchId, start, end])

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    new EngagementService()
      .getEngagementAnalytics(engagementId, { objectiveId, audienceId, launchId, start, end })
      .then((response) => {
        if (!cancelled) setChartData(response)
      })
      .catch((error) => {
        console.error("Monitor analytics:", error)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
    // Keyed on the formatted dates rather than the Date objects: two Dates for the
    // same day are never ===, so depending on `range` would refetch on every render.
  }, [engagementId, objectiveId, audienceId, launchId, start, end])

  /*
      Content Catalog tags for the engagement's ads, in one request (MPN-1506). Keyed by
      ad id and read by the ad tiles. Scoped to the engagement rather than the objective so
      it survives switching campaigns without refetching, and kept out of the analytics
      chain so a tag failure cannot blank the charts.
  */
  useEffect(() => {
    let cancelled = false
    new EngagementService()
      .getEngagementAdTags(engagementId)
      .then((tags) => {
        if (!cancelled) setAdTags(tags)
      })
      .catch(() => {
        if (!cancelled) setAdTags({})
      })
    return () => {
      cancelled = true
    }
  }, [engagementId])

  /*
      Only to name the audience in the head. Arriving from an audience row means
      the URL carries an id and nothing else, and a view filtered to something
      unnamed is a view you can't trust. Skipped entirely when no audience is
      scoped, which is the common case.
  */
  useEffect(() => {
    if (!audienceId) {
      setAudienceName(null)
      return
    }
    let cancelled = false
    new AdGroupService()
      .listAdGroups(engagementId)
      .then((groups) => {
        if (cancelled) return
        const match = groups.find((g) => g.audience_definition_id === audienceId)
        setAudienceName(match?.audience_name ?? `Audience #${audienceId}`)
      })
      .catch(() => {
        if (!cancelled) setAudienceName(`Audience #${audienceId}`)
      })
    return () => {
      cancelled = true
    }
  }, [engagementId, audienceId])

  const charts = chartData.charts

  /*
      The campaign's flight (§1.1). It arrives with the charts rather than in its own
      request, and it feeds the "Since Campaign Start Date" preset as well as the
      header. Deliberately not a dependency of the fetch above: it is an OUTPUT of
      that request, so depending on it would refetch on every response.
  */
  const campaignDates = (charts as any)?.["campaign-dates"] ?? {}
  const campaignStart = parseApiDate(campaignDates.start_date)

  /*
      Apply the default once the start date it needs actually arrives (MPN-1639).

      "Since Campaign Start Date" resolves against the campaign's start, and that start
      is an OUTPUT of the analytics request this range drives -- so on first render
      there is nothing to resolve against and the range falls back to all time. The
      dropdown read "Since Campaign Start Date" while the page showed every day on
      record, and choosing that same option by hand changed the numbers: the label and
      the data disagreed until someone touched the control.

      Only while the filter is still on the default. Once someone picks a range, a date
      arriving from a response is not a reason to overrule them.

      The equality guard is what stops this cycling: setting the range refetches, the
      response rebuilds campaignStart as a new Date, and without the guard that would
      set the range again forever.
  */
  useEffect(() => {
    if (filterKey !== "sinceStart" || !campaignStart) return
    const next = resolveDateFilter("sinceStart", { campaignStart })
    setRange((prev) => (toApiDate(prev.start) === toApiDate(next.start) ? prev : next))
  }, [filterKey, campaignStart?.getTime()])

  return (
    <div className="mon-pane mon-dash">
      <div className="mon-dash__head">
        <button type="button" className="mon-dash__back" onClick={onBack}>
          <i className="bi bi-chevron-left" /> All campaigns
        </button>
        <span className="mon-dash__title">{title}</span>
        <span className="mon-dash__scope">{scopeLabel}</span>
        {audienceName && <span className="mon-dash__scope">Audience · {audienceName}</span>}
      </div>

      <div className="mon-dash__meta">
        {/*
            "Campaign Start Date" is the go-live stamp (MPN-1578, replacing the Scheduled
            Start chip that used to sit here): when this campaign actually first published,
            which is the boundary billing reads. MPN-1498's warning still holds and is why
            the label stays qualified -- an unqualified "Start Date" beside "Scheduled End"
            is how someone reconciles an invoice against the wrong date.

            The scheduled start has not gone away, it is just no longer displayed here. It
            still comes down in this payload and still sets the Date Filter's baseline
            below, because pace and billing are different questions.
        */}
        <MonitorFlightDate
          label="Campaign Start Date"
          value={campaignDates.go_live_date}
          format={formatStartDate}
        />
        <MonitorFlightDate label="Scheduled End" value={campaignDates.end_date} />
        <MonitorDateFilter
          value={filterKey}
          range={range}
          campaignStart={campaignStart}
          onChange={(key, next) => {
            setFilterKey(key)
            setRange(next)
          }}
        />
      </div>

      {/*
          Above the tabs and outside the isLoading branch below, so it holds its place
          while a new range loads instead of the tab strip jumping up and back down
          on every filter change (§2: it stays visible regardless of tab).
      */}
      <PerformanceSnapshot data={charts} />

      <div className="mon-tabs" role="tablist">
        {SECTIONS.filter((tab) => !("feature" in tab) || canSeeEmailSms).map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={section === tab.key}
            className={`mon-tab${section === tab.key ? " mon-tab--on" : ""}`}
            onClick={() => setSection(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <div className={`mon-dash__body${section === "emailSms" ? " mon-dash__body--wide" : ""}`}>
          <div className="mon-dash__main">
            {section === "conversions" && <Conversions data={charts} />}
            {section === "spend" && <Spend data={charts} launchId={launchId} />}
            {section === "revenue" && <Revenue data={charts} />}
            {section === "audience" && <Audience data={charts} />}
            {section === "ads" && <AdPerformance data={charts} adTags={adTags} />}
            {section === "emailSms" && (
              <EmailSmsPerformance
                data={emailSms}
                channelsOn={emailSmsOn}
                hasCampaign={Boolean(launchId)}
                failed={emailSmsFailed}
                onOpenCampaign={
                  launchId
                    ? () =>
                        window.location.assign(
                          `/engagements/${engagementId}/email-sms/${launchId}`,
                        )
                    : undefined
                }
              />
            )}
          </div>
          {/* Management is about ad delivery -- sync, key dates, the last file load. None of
              it applies to a sequence send, and the design runs this tab full width. */}
          {section !== "emailSms" && (
          <aside className="mon-dash__side">
            <div className="mon-dash__side-label">Management</div>
            <Management data={charts} />
          </aside>
          )}
        </div>
      )}
    </div>
  )
}


/*
    One end of the campaign's flight (MPN-1530 §1.1). Reads "Not scheduled" rather
    than blank when a campaign has no ad groups on the Schedule yet: an empty chip
    beside a populated one looks like a failed load.
*/
function MonitorFlightDate({
  label,
  value,
  format = formatFlightDate,
}: {
  label: string
  value?: string | null
  format?: (value: string | null | undefined) => string
}) {
  return (
    <div className="mon-dash__metachip">
      <div className="mon-dash__metachip-label">{label}</div>
      <div className="mon-dash__metachip-value">{format(value)}</div>
    </div>
  )
}
